package com.oasisfeng.invaluable.social

import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

/**
 * Transport-agnostic zero-config peer discovery: automates the WebRTC offer/answer exchange a human (or a signaling
 * server) would otherwise do, over a pluggable [SignalingBus]. Once the handshake completes the real
 * [WebRtcPeerManager] + [PeerSync] DataChannel takes over — the same transport used cross-machine — so the bus only
 * carries SDP/ICE to bootstrap; data flows peer to peer (still serverless).
 *
 * One protocol drives every form (TUI / MCP / Web); they differ only in which [SignalingBus] they plug in:
 * - `presence` heartbeats advertise a node; a peer is dropped once its heartbeat goes stale.
 * - For a pair, the lexicographically smaller peerId is the **offerer**, the larger the **answerer** — no glare.
 * - Offerer: `createOffer()` → post `offer` → await the matching `answer` → `applyAnswer`.
 * - Answerer: on a matching `offer`, `acceptOffer()` → post `answer`.
 * Handshakes are serialized (one at a time) so [WebRtcPeerManager.applyAnswer]'s "most recent pending outbound" is
 * unambiguous; a per-process `instance` lets a restarted/reloaded peer be re-handshaked.
 *
 * @param acceptTransportCandidate policy gate for peer-advertised transport candidates. Candidates are untrusted
 * local observations, not configured seeds, so the default denies all of them. A caller may opt in only after checking
 * the candidate's namespace and provenance; otherwise a peer from another relay room can silently cross that boundary.
 */
class PeerDiscovery(
    private val selfId: String,
    private val name: String,
    private val peerManager: () -> WebRtcPeerManager,
    private val scope: CoroutineScope,
    private val onChange: () -> Unit,
    private val log: (String) -> Unit,
    private val bus: SignalingBus,
    private val transportCandidates: () -> List<String> = { emptyList() },
    private val onTransportCandidates: (List<String>) -> Unit = {},
    private val acceptTransportCandidate: (String) -> Boolean = { false },
    private val fastPollMs: Int = 2000,
    private val slowPollMs: Int = 30_000,
    private val quietBeforeBackoffMs: Double = 10_000.0,
    private val staleMs: Double = 90_000.0,
    private val handshakeTimeoutMs: Long = 15_000,
) {
    private val instance: String = js("Math.random().toString(36).slice(2)") as String

    private var timer: dynamic = null
    private var running = false
    private var busy = false
    private var lastTopologyChangeAt: Double = 0.0

    /** Peers currently advertised on the bus, keyed by peerId. */
    private val peers = HashMap<String, Peer>()

    /** Peers we've completed a handshake with, keyed by id@instance so a restarted/reloaded peer is re-handshaked. */
    private val handshaked = HashSet<String>()

    /** The single outbound handshake in flight: completed when its matching answer arrives. */
    private var pendingAnswer: CompletableDeferred<String>? = null
    private var pendingAnswerFrom: String? = null

    val available: Boolean get() = bus.available
    val isRunning: Boolean get() = running

    /** Number of peers we've completed a handshake with (diagnostics / tests). */
    val handshakeCount: Int get() = handshaked.size

    fun start() {
        if (running) return
        if (!bus.available) { log("discovery: no signaling transport available — auto-connect off (use copy/paste)"); return }
        running = true
        bus.start { runCatching { handle(it) } }
        broadcast("presence")
        log("discovery: ${bus.label} (auto WebRTC signaling)")
        lastTopologyChangeAt = js("Date.now()") as Double
        scheduleNextPoll(fastPollMs)
    }

    fun stop() {
        if (!running) return
        running = false
        runCatching { js("clearTimeout")(timer) }
        runCatching { broadcast("bye") }
        runCatching { bus.close() }
        peers.clear()
    }

    /** Status lines: which transport, how many handshakes completed, and which peers are currently advertised. */
    fun status(): List<String> {
        if (!running) return listOf(if (bus.available) "discovery: off" else "discovery: unavailable (no signaling transport)")
        val seen = peers.values.map {
            val transports = if (it.transportCandidates.isEmpty()) "" else " transports=${it.transportCandidates.size}"
            "${it.name}(${it.id.take(6)})$transports"
        }
        return listOf(
            "auto WebRTC signaling via ${bus.label}  handshaked=${handshaked.size}",
            "seen: " + (if (seen.isEmpty()) "(none)" else seen.joinToString(", ")),
        )
    }

    // ───── timer ─────
    //
    // Adaptive cadence, not a fixed setInterval (app-social/README.md, "Inbox and change notification"): a peer's own poll tick does
    // synchronous work (broadcast + prune + maybeOffer) on Node's single JS thread, which used to run unconditionally
    // every 2s for the life of any `mcp`-mode process and contended with wait_inbox_update's own timers. Discovery
    // responsiveness does not depend on this node's own tick cadence at all — start() already broadcasts once
    // unconditionally, and onPresence/onOffer/onAnswer/"bye" are all reacted to instantly, pushed straight from the
    // inbound bus callback (see handle()) — so backing off this node's *own* re-announce/re-scan rate once nothing new
    // has happened for a while (quietBeforeBackoffMs) is safe: it only cuts steady-state cost, never first-contact
    // latency for a peer that shows up later.

    private fun scheduleNextPoll(delayMs: Int) { timer = js("setTimeout")(fun() { poll() }, delayMs) }

    private fun poll() {
        if (!running) return
        broadcast("presence")
        pruneStale()
        maybeOffer()
        val quiet = (js("Date.now()") as Double) - lastTopologyChangeAt < quietBeforeBackoffMs
        scheduleNextPoll(if (quiet) fastPollMs else slowPollMs)
    }

    /**
     * Marks "something about the mesh's topology just changed" — resets the quiet clock that gates backing off to
     * [slowPollMs]. Deliberately narrower than "any inbound message": a routine presence beat from an already-known,
     * already-handshaked peer must NOT reset this, or two long-lived stable peers would keep re-arming each other's
     * fast timer forever and steady-state backoff would never engage between exactly two nodes.
     */
    private fun markTopologyChange() { lastTopologyChangeAt = js("Date.now()") as Double }

    private fun pruneStale() {
        val now = js("Date.now()") as Double
        val stale = peers.values.filter { now - it.lastSeen > staleMs }.map { it.id }
        if (stale.isNotEmpty()) { stale.forEach { peers.remove(it) }; markTopologyChange(); onChange() }
    }

    /** Offer to a live, larger-id peer we haven't handshaked yet (the smaller id is always the offerer). */
    private fun maybeOffer() {
        if (busy) return
        peers.values.firstOrNull { it.id > selfId && it.key !in handshaked }?.let { offer(it) }
    }

    // ───── inbound messages ─────

    private fun handle(data: dynamic) {
        val from = data?.from as? String ?: return
        if (from == selfId) return
        when (data.kind as? String) {
            "presence" -> onPresence(from, data)
            "offer" -> onOffer(from, data)
            "answer" -> onAnswer(from, data)
            "bye" -> if (peers.remove(from) != null) { markTopologyChange(); onChange() }
        }
    }

    private fun onPresence(from: String, data: dynamic) {
        val now = js("Date.now()") as Double
        val known = peers.containsKey(from)
        val candidates = transportCandidateList(data)
        peers[from] = Peer(from, (data.name as? String) ?: "peer", (data.instance as? String) ?: "?", now, candidates)
        val acceptedCandidates = candidates.filter(acceptTransportCandidate)
        if (acceptedCandidates.isNotEmpty()) onTransportCandidates(acceptedCandidates)
        if (!known) { broadcast("presence"); markTopologyChange(); onChange() }   // greet a newly-seen peer so both sides converge fast
        maybeOffer()
    }

    private fun onOffer(from: String, data: dynamic) {
        if ((data.answerer as? String) != selfId) return
        if (from >= selfId) return   // only the smaller id offers; ignore anything else (no glare)
        val blob = data.blob as? String ?: return
        val peer = peers[from] ?: Peer(
            from,
            (data.name as? String) ?: "peer",
            (data.instance as? String) ?: "?",
            js("Date.now()") as Double,
            transportCandidateList(data),
        )
        if (peer.key in handshaked || busy) return
        answer(peer, blob)
    }

    private fun onAnswer(from: String, data: dynamic) {
        if ((data.offerer as? String) != selfId || pendingAnswerFrom != from) return
        (data.blob as? String)?.let { pendingAnswer?.complete(it) }
    }

    // ───── handshake halves ─────

    private fun offer(peer: Peer) {
        busy = true
        markTopologyChange()
        scope.launch {
            try {
                val deferred = CompletableDeferred<String>()
                pendingAnswer = deferred
                pendingAnswerFrom = peer.id
                val offerBlob = peerManager().createOffer()
                broadcast("offer") { it.offerer = selfId; it.answerer = peer.id; it.blob = offerBlob }
                log("discovery: offered ${peer.name}; awaiting answer")
                val answerBlob = withTimeoutOrNull(handshakeTimeoutMs) { deferred.await() }
                if (answerBlob != null) {
                    peerManager().applyAnswer(answerBlob)
                    handshaked += peer.key
                    log("discovery: connected to ${peer.name}(${peer.id.take(6)})")
                    onChange()
                } else {
                    log("discovery: ${peer.name} did not answer in time")
                }
            } catch (t: Throwable) {
                log("discovery: offer to ${peer.name} failed: ${t.message}")
            } finally {
                pendingAnswer = null; pendingAnswerFrom = null; busy = false
            }
        }
    }

    private fun answer(peer: Peer, offerBlob: String) {
        busy = true
        markTopologyChange()
        scope.launch {
            try {
                val answerBlob = peerManager().acceptOffer(offerBlob)
                broadcast("answer") { it.offerer = peer.id; it.answerer = selfId; it.blob = answerBlob }
                handshaked += peer.key
                log("discovery: answered ${peer.name}(${peer.id.take(6)})")
                onChange()
            } catch (t: Throwable) {
                log("discovery: answer to ${peer.name} failed: ${t.message}")
            } finally {
                busy = false
            }
        }
    }

    // ───── message plumbing ─────

    private class Peer(
        val id: String,
        val name: String,
        val instance: String,
        var lastSeen: Double,
        val transportCandidates: List<String>,
    ) {
        val key: String get() = "$id@$instance"
    }

    private fun broadcast(kind: String, fill: (dynamic) -> Unit = {}) {
        val msg = js("{}")
        msg.kind = kind; msg.from = selfId; msg.name = name; msg.instance = instance
        val candidates = transportCandidates().distinct()
        if (candidates.isNotEmpty()) msg.transportCandidates = candidates.toTypedArray()
        fill(msg)
        runCatching { bus.post(msg) }
    }

    private fun transportCandidateList(data: dynamic): List<String> {
        val raw = data.transportCandidates ?: return emptyList()
        val length = raw.length as? Int ?: return emptyList()
        val candidates = ArrayList<String>(length)
        for (i in 0 until length) {
            val candidate = raw[i] as? String ?: continue
            if (candidate.isNotBlank()) candidates += candidate
        }
        return candidates.distinct()
    }
}
